import { Intent } from '@clarion/ClarionIcon';
import { nanoid } from 'nanoid';

// Time tracking types
export type TimeEntry = {
  id: string;
  assignmentId: string;
  userId: string;
  userName: string;
  startTime: number;
  endTime?: number;
  duration?: number; // Duration in milliseconds (useful for manual entries)
  notes?: string;
  isManualEntry?: boolean;
};

export const ASSIGNMENT_STATUSES = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  blocked: 'Blocked',
  cancelled: 'Cancelled',
} as const;
export type AssignmentStatus = keyof typeof ASSIGNMENT_STATUSES;

export const ASSIGNMENT_PRIORITIES = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
} as const;
export type AssignmentPriority = keyof typeof ASSIGNMENT_PRIORITIES;

export const ASSIGNMENT_TYPES = {
  transcription_edit: 'Transcription edit',
  translation_review: 'Translation review',
  voiceover_request: 'Voiceover request',
  embed_in_lms: 'Embed in LMS',
  publish_captions: 'Publish captions',
  create_audio_description: 'Create audio description',
  edit_audio_description: 'Edit audio description',
  translate_audio_description: 'Translate audio description',
  publish_audio_description: 'Publish audio description',
  review_corrections: 'Review corrections',
  custom: 'Custom task',
} as const;
export type AssignmentType = keyof typeof ASSIGNMENT_TYPES;

export type AssignmentActivity = {
  id: string;
  assignmentId: string;
  timestamp: number;
  userId: string;
  userName: string;
  action: string;
  details?: Record<string, any>;
};

export type FileLabel = {
  id: string;
  fileId: string;
  workspaceId: string;
  name: string;
  color: string;
  createdAt: number;
  createdBy: string;
};

export type Assignment = {
  id: string;
  fileId: string;
  workspaceId: string;
  title: string;
  description?: string;
  type: AssignmentType;
  status: AssignmentStatus;
  priority: AssignmentPriority;
  createdAt: number;
  createdBy: string;
  dueDate?: number;
  assignees: string[]; // Array of user IDs
  labels?: string[]; // Array of label IDs
  completedAt?: number;
  completedBy?: string;
  activities?: AssignmentActivity[];
  customFields?: Record<string, any>;
  timeEntries?: TimeEntry[]; // Array of time tracking entries
  totalTimeSpent?: number; // Total time spent in milliseconds
};

export function getAssignmentTypeLabel(type: AssignmentType): string {
  return ASSIGNMENT_TYPES[type] || type;
}

export function getAssignmentStatusLabel(status: AssignmentStatus): string {
  return ASSIGNMENT_STATUSES[status] || status;
}

export function getAssignmentPriorityLabel(priority: AssignmentPriority): string {
  return ASSIGNMENT_PRIORITIES[priority] || priority;
}

export function getAssignmentStatusIntent(status: AssignmentStatus): Intent {
  const intents: Record<AssignmentStatus, Intent> = {
    pending: 'none',
    in_progress: 'primary',
    completed: 'success',
    blocked: 'warning',
    cancelled: 'danger',
  };

  return intents[status] || 'none';
}

export function getAssignmentPriorityIntent(priority: AssignmentPriority): Intent {
  const intents: Record<AssignmentPriority, Intent> = {
    low: 'success',
    medium: 'primary',
    high: 'warning',
    urgent: 'danger',
  };

  return intents[priority] || 'none';
}

export function createTimeEntry(
  assignmentId: string,
  userId: string,
  userName: string,
  startTime?: number,
  endTime?: number,
  duration?: number,
  notes?: string,
  isManualEntry: boolean = false,
): TimeEntry {
  const now = Date.now();

  return {
    id: nanoid(),
    assignmentId,
    userId,
    userName,
    startTime: startTime || now,
    endTime,
    duration,
    notes,
    isManualEntry,
  };
}

// Format milliseconds as HH:MM:SS
export function formatTimeSpent(ms: number): string {
  if (!ms) return '00:00:00';

  const seconds = Math.floor((ms / 1000) % 60);
  const minutes = Math.floor((ms / (1000 * 60)) % 60);
  const hours = Math.floor(ms / (1000 * 60 * 60));

  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    seconds.toString().padStart(2, '0'),
  ].join(':');
}

// Calculate total time spent from time entries
export function calculateTotalTimeSpent(timeEntries: TimeEntry[] = []): number {
  return timeEntries.reduce((total, entry) => {
    // For completed entries
    if (entry.duration) {
      return total + entry.duration;
    }

    // For entries with start and end times
    if (entry.startTime && entry.endTime) {
      return total + (entry.endTime - entry.startTime);
    }

    return total;
  }, 0);
}

// Helper function to find active time entry for a specific user
export function getActiveTimeEntry(assignment: Assignment, userId: string): TimeEntry | undefined {
  if (!assignment?.timeEntries) return undefined;

  // Find the most recent time entry for this user that doesn't have an end time
  return assignment.timeEntries.find(
    (entry) => entry.userId === userId && entry.startTime && !entry.endTime,
  );
}

// Helper to check if an assignment has any active time entries for the given user
export function hasActiveTimer(assignment: Assignment, userId: string): boolean {
  return Boolean(getActiveTimeEntry(assignment, userId));
}

// Helper to get all active time entries across assignments for a specific user
export function getAllActiveTimeEntries(
  assignments: Assignment[],
  userId: string,
): { assignment: Assignment; timeEntry: TimeEntry }[] {
  if (!assignments || !userId) return [];

  return assignments.reduce(
    (active, assignment) => {
      const activeEntry = getActiveTimeEntry(assignment, userId);
      if (activeEntry) {
        active.push({
          assignment,
          timeEntry: activeEntry,
        });
      }
      return active;
    },
    [] as { assignment: Assignment; timeEntry: TimeEntry }[],
  );
}
